import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { placeStone, getStoneCount } from '../../services/memorialApi';
import './StoneButton.css';

// Storage key for personal placed stones (to track which ones YOU placed)
const STORAGE_KEY = 'memorialPlacedStones';

/**
 * Get personal placed stones from localStorage
 * @returns {Object} Map of memorial IDs to stone data
 */
const getPlacedStones = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading placed stones from localStorage:', e);
  }
  return {};
};

/**
 * Save personal placed stone to localStorage
 * @param {number} memorialId - The memorial ID
 */
const savePersonalStone = (memorialId) => {
  try {
    const stones = getPlacedStones();
    stones[memorialId] = {
      placed: true,
      placedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stones));
    return stones;
  } catch (e) {
    console.error('Error saving placed stone to localStorage:', e);
    return null;
  }
};

/**
 * Check if the current user has placed a stone for a memorial
 * @param {number} memorialId - The memorial ID
 * @returns {boolean} True if the user has placed a stone
 */
export const isStonePlaced = (memorialId) => {
  const stones = getPlacedStones();
  return !!stones[memorialId];
};

/**
 * Stone SVG icon component
 */
const StoneIcon = ({ placed, className = '' }) => (
  <svg
    className={`stone-icon ${className}`}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Stone shape - organic, rounded pebble */}
    <ellipse
      cx="12"
      cy="14"
      rx="9"
      ry="6"
      fill={placed ? '#8b7355' : '#c4b8a8'}
      stroke={placed ? '#5b4636' : '#8b7355'}
      strokeWidth="1"
    />
    {/* Highlight/shine on stone */}
    <ellipse
      cx="9"
      cy="12"
      rx="3"
      ry="1.5"
      fill="rgba(255, 255, 255, 0.3)"
    />
    {/* Shadow beneath stone */}
    {placed && (
      <ellipse
        cx="12"
        cy="19"
        rx="7"
        ry="1.5"
        fill="rgba(0, 0, 0, 0.15)"
      />
    )}
  </svg>
);

StoneIcon.propTypes = {
  placed: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * StoneButton component for leaving a stone in memory
 * Based on the Jewish tradition of placing stones on graves
 * @param {Object} props - Component props
 * @param {number} props.memorialId - The memorial ID
 * @param {string} props.memorialName - The name of the person (for accessibility)
 * @param {Function} props.onPlaced - Callback when stone is placed
 */
const StoneButton = ({ memorialId, memorialName, onPlaced }) => {
  const [isPlaced, setIsPlaced] = useState(false); // Personal: did YOU place it
  const [count, setCount] = useState(0); // Shared: total stones placed by everyone
  const [showTooltip, setShowTooltip] = useState(false);
  const [justPlaced, setJustPlaced] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check personal state and fetch shared count on mount
  useEffect(() => {
    const personallyPlaced = isStonePlaced(memorialId);
    setIsPlaced(personallyPlaced);

    // Fetch shared count from server
    getStoneCount(memorialId)
      .then((serverCount) => {
        setCount(serverCount);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [memorialId]);

  // Handle placing the stone
  const handlePlace = useCallback(async () => {
    if (!isPlaced) {
      // Save personal record
      savePersonalStone(memorialId);
      setIsPlaced(true);
      setJustPlaced(true);
      setShowTooltip(true);

      // Update shared count on server
      try {
        const result = await placeStone(memorialId);
        setCount(result.count);
      } catch (error) {
        // Still show as placed locally even if server fails
        setCount((prev) => prev + 1);
      }

      // Hide tooltip after 3 seconds
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      // Reset "just placed" animation after it completes
      setTimeout(() => {
        setJustPlaced(false);
      }, 1000);

      // Call the onPlaced callback if provided
      if (onPlaced) {
        onPlaced(memorialId);
      }
    }
  }, [isPlaced, memorialId, onPlaced]);

  const hasAnyStones = count > 0 || isPlaced;

  return (
    <div className="stone-button-container">
      <button
        className={`stone-button ${hasAnyStones ? 'placed' : ''} ${justPlaced ? 'just-placed' : ''}`}
        onClick={handlePlace}
        disabled={isPlaced}
        aria-label={
          isPlaced
            ? `You placed a stone in memory of ${memorialName}. ${count} total stones placed.`
            : `Leave a stone in memory of ${memorialName}. ${count} stones already placed.`
        }
        aria-pressed={isPlaced}
        title={isPlaced ? `Your stone is placed (${count} total)` : `Leave a stone (${count} placed)`}
      >
        <StoneIcon placed={hasAnyStones} className={justPlaced ? 'placing' : ''} />
      </button>

      {/* Count badge */}
      {!isLoading && count > 0 && (
        <span className="stone-count" aria-hidden="true">
          {count}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div className="stone-tooltip" role="status" aria-live="polite">
          {isPlaced ? 'Your stone is placed' : `${count} stones placed`}
        </div>
      )}
    </div>
  );
};

StoneButton.propTypes = {
  memorialId: PropTypes.number.isRequired,
  memorialName: PropTypes.string.isRequired,
  onPlaced: PropTypes.func,
};

export default StoneButton;
export { getPlacedStones };
